import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { IMAGES } from "../data/site";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-36 bg-brand-cream overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* Image collage */}
        <motion.div
          {...fadeUp}
          className="lg:col-span-6 relative h-[520px] md:h-[620px]"
        >
          <div className="absolute top-0 left-0 w-[62%] h-[68%] overflow-hidden rounded-3xl shadow-2xl">
            <img
              src={IMAGES.aboutA}
              alt="Family-style dining at Red Chilly's"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
            />
          </div>
          <div className="absolute bottom-0 right-0 w-[58%] h-[58%] overflow-hidden rounded-3xl shadow-2xl border-8 border-brand-cream">
            <img
              src={IMAGES.aboutB}
              alt="Warm restaurant interior"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
            />
          </div>
          {/* Floating badge */}
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            whileInView={{ scale: 1, rotate: -8 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.4, type: "spring" }}
            className="absolute top-6 right-2 md:right-8 w-32 h-32 md:w-40 md:h-40 bg-brand-red text-brand-cream rounded-full flex flex-col items-center justify-center text-center shadow-2xl"
          >
            <Flame size={24} className="text-brand-mustard mb-1" />
            <div className="font-display text-3xl md:text-4xl leading-none">10+</div>
            <div className="font-heading uppercase text-[9px] md:text-[10px] tracking-[0.2em] font-bold mt-1 px-3">
              Years of Flavour
            </div>
          </motion.div>
        </motion.div>

        {/* Text */}
        <div className="lg:col-span-6">
          <motion.div {...fadeUp}>
            <span className="font-heading uppercase text-[11px] tracking-[0.3em] font-bold text-brand-red">
              · Our Story
            </span>
            <h2 className="mt-4 font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9] text-brand-charcoal">
              More than a<br />
              <span className="text-brand-red italic">restaurant.</span>
            </h2>
          </motion.div>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-8 text-lg text-brand-charcoal/75 leading-relaxed text-balance"
          >
            A family kitchen where bold flavours, fresh ingredients, and unforgettable
            moments come together. For over a decade we've served Pittsburgh the recipes
            we grew up with — the kind of cooking you can only learn from a grandmother
            who measures spice with her palm, not a spoon.
          </motion.p>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-5 text-base text-brand-charcoal/65 leading-relaxed"
          >
            From the dum-cooked biryani to the sizzling tandoor, every dish is built
            from scratch each morning. No shortcuts. No frozen pastes. Just street-food
            soul, served on a clean plate.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.35 }}
            className="mt-10 flex items-center gap-6 pt-8 border-t border-brand-charcoal/15"
          >
            <div className="font-display text-3xl md:text-4xl text-brand-charcoal italic">
              — The Red Chilly's Family
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.45 }}
            className="mt-8 grid grid-cols-3 gap-6"
          >
            {[
              { n: "100%", l: "Fresh Daily" },
              { n: "14", l: "Menu Categories" },
              { n: "Family", l: "Owned & Run" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-brand-red pl-4">
                <div className="font-display text-2xl md:text-3xl text-brand-charcoal">
                  {s.n}
                </div>
                <div className="font-heading uppercase text-[10px] tracking-[0.2em] font-bold mt-1 text-brand-charcoal/60">
                  {s.l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
